import React, { useState, useMemo } from 'react';
import { isApplicationActive } from '../../lib/constants';
import { getApplicantTimezone } from '../../lib/timezone';
import { FolderOpen, Plus, Archive, ChevronRight, Users, Calendar, ArrowRight, Search, X, List } from 'lucide-react';

interface ProgramCardHubProps {
  programs: any[];
  applications: any[];
  onSelectProgram: (programId: string) => void;
  onOpenArchived: () => void;
  archivedCount: number;
  onCreateCandidate?: () => void;
  onTableView?: () => void;
}

export const ProgramCardHub: React.FC<ProgramCardHubProps> = ({
  programs,
  applications,
  onSelectProgram,
  onOpenArchived,
  archivedCount,
  onCreateCandidate,
  onTableView,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Compute today's start timestamp in the local workspace timezone
  const todayStartMs = useMemo(() => {
    const tz = getApplicantTimezone();
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';
    const dateStr = `${getPart('year')}-${getPart('month')}-${getPart('day')}T00:00:00`;
    return new Date(dateStr).getTime();
  }, []);

  // Filter active applications only
  const activeApplications = useMemo(() => {
    return (applications || []).filter(app => isApplicationActive(app));
  }, [applications]);

  // Aggregate stats per program - optimized to avoid nested loops
  const programStats = useMemo(() => {
    const statsMap: Record<string, { totalActive: number; todayCount: number }> = {};
    
    // Create a mapping from program identifiers to program objects for quick lookup
    const programIdMap = new Map<string, any>();
    (programs || []).forEach(p => {
      const primaryKey = p.id || p.programId || p.name;
      if (primaryKey && !statsMap[primaryKey]) {
        statsMap[primaryKey] = { totalActive: 0, todayCount: 0 };
      }
      const keys = [p.id, p.programId, p.name].filter(Boolean);
      keys.forEach(key => {
        if (key) {
          programIdMap.set(key.toLowerCase(), p);
        }
      });
    });

    // Single pass through applications to accumulate stats
    (activeApplications || []).forEach(app => {
      const appId = app.programId || app.programType || '';
      const appName = app.programType || app.programName || '';
      const createdMs = app.createdAt ? new Date(app.createdAt).getTime() : 0;
      const isToday = createdMs >= todayStartMs;

      // Check if this application belongs to any program
      const matchedProgram = 
        programIdMap.get(appId.toLowerCase()) || 
        programIdMap.get(appName.toLowerCase());

      if (matchedProgram) {
        const primaryKey = matchedProgram.id || matchedProgram.programId || matchedProgram.name;
        if (primaryKey && statsMap[primaryKey]) {
          statsMap[primaryKey].totalActive += 1;
          if (isToday) {
            statsMap[primaryKey].todayCount += 1;
          }
        }
      }
    });

    return statsMap;
  }, [programs, activeApplications, todayStartMs]);

  // Handle programs array normalization and search filtering
  const filteredProgramsList = useMemo(() => {
    if (!programs || programs.length === 0) return [];
    const activeList = programs.filter(p => p.active !== false);
    if (!searchQuery.trim()) return activeList;
    const q = searchQuery.toLowerCase().trim();
    return activeList.filter(p => {
      const name = (p.name || p.title || '').toLowerCase();
      const progId = (p.id || p.programId || '').toLowerCase();
      const code = (p.code || '').toLowerCase();
      const shortCode = (p.shortCode || '').toLowerCase();
      const programCode = (p.programCode || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      return name.includes(q) || progId.includes(q) || code.includes(q) || shortCode.includes(q) || programCode.includes(q) || desc.includes(q);
    });
  }, [programs, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Hub Header & Action Controls */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-brand" />
            <h2 className="text-lg font-black text-slate-900 tracking-tight">Applicant Directory & Program Hub</h2>
          </div>
          <p className="text-xs text-slate-500 font-medium mt-1">
            Select a program to drill down into its isolated active applicant pipeline.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Program Cards Search Bar */}
          <div className="relative min-w-[240px] sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search programs..."
              className="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 focus:bg-white focus:border-brand rounded-xl text-xs font-semibold text-slate-900 placeholder:text-slate-400 outline-none transition-all shadow-2xs"
            />
            {searchQuery && (
              <button aria-label="Clear search" title="Clear search"
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-md cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {onTableView && (
            <button
              type="button"
              onClick={onTableView}
              className="px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl transition-all flex items-center gap-2 cursor-pointer"
            >
              <List className="w-4 h-4 text-slate-500" />
              <span>Table View</span>
            </button>
          )}

          <button
            type="button"
            onClick={onOpenArchived}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200/80 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all flex items-center gap-2 cursor-pointer"
          >
            <Archive className="w-4 h-4 text-slate-500" />
            <span>Archived Applications</span>
            <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-black">
              {archivedCount}
            </span>
          </button>

          {onCreateCandidate && (
            <button
              type="button"
              onClick={onCreateCandidate}
              className="px-4 py-2 bg-brand hover:bg-brand/90 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Add Candidate</span>
            </button>
          )}
        </div>
      </div>

      {/* Program Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredProgramsList.map((program) => {
          const progId = program.id || program.programId;
          const name = program.name || 'Unnamed Program';
          const isOpen = program.active ?? program.isOpen ?? true;

          const matchStats = programStats[progId] || programStats[name] || { totalActive: 0, todayCount: 0 };

          return (
            <div
              key={progId}
              onClick={() => onSelectProgram(progId)}
              className="bg-white border border-slate-200/90 hover:border-brand/60 rounded-2xl p-5 transition-all duration-200 shadow-2xs hover:shadow-md cursor-pointer group flex flex-col justify-between space-y-5"
            >
              {/* Header: Title & Status Badge */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 group-hover:text-brand transition-colors line-clamp-2">
                    {name}
                  </h3>
                  {(program.code || program.shortCode || program.programCode) && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-md bg-slate-100 font-mono text-[10px] font-bold text-slate-600 border border-slate-200">
                      {program.code || program.shortCode || program.programCode}
                    </span>
                  )}
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border shrink-0 ${
                    isOpen
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200/80'
                      : 'bg-rose-50 text-rose-700 border-rose-200/80'
                  }`}
                >
                  {isOpen ? 'OPEN' : 'CLOSED'}
                </span>
              </div>

              {/* Program Metrics */}
              <div className="space-y-2.5 pt-1">
                <div className="flex items-center justify-between text-xs py-1.5 px-3 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-2 text-slate-600 font-semibold">
                    <Users className="w-3.5 h-3.5 text-slate-400" />
                    <span>Total Active Applications</span>
                  </div>
                  <span className="font-extrabold font-mono text-slate-900 text-sm">
                    {matchStats.totalActive}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs py-1.5 px-3 bg-blue-50/50 rounded-xl border border-blue-100/60">
                  <div className="flex items-center gap-2 text-blue-800 font-semibold">
                    <Calendar className="w-3.5 h-3.5 text-blue-500" />
                    <span>Applications Today</span>
                  </div>
                  <span className="font-extrabold font-mono text-blue-700 text-sm">
                    +{matchStats.todayCount}
                  </span>
                </div>
              </div>

              {/* Action Footer */}
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 group-hover:text-slate-600 transition-colors">
                  Isolate Pipeline
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-brand group-hover:translate-x-1 transition-transform">
                  Enter Program <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          );
        })}

        {filteredProgramsList.length === 0 && (
          <div className="col-span-full bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-12 text-center space-y-3">
            <FolderOpen className="w-10 h-10 text-slate-400 mx-auto" />
            <h4 className="text-sm font-bold text-slate-700">
              {searchQuery ? `No programs matching "${searchQuery}"` : 'No active programs found'}
            </h4>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              {searchQuery ? 'Try adjusting your search query or clear the filter.' : 'Configure programs in settings to populate your program card directory.'}
            </p>
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="px-3.5 py-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer inline-flex items-center gap-1.5 mt-2"
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramCardHub;
